import AuthCardWrapper from 'auth/shared/AuthCardWrapper'
import { observer } from 'mobx-react-lite'
import CenterFullScreen from 'reusable/mui/utilities/CenterFullScreen'
import { useAuthStore } from 'auth/AuthStore'
import AuthShareStore, { AuthShareStoreProvider, useAuthShareStore } from './AuthShareStore'
import useStore from 'reusable/mobx-store-utils'
import useInitialMount from 'reusable/hooks/useInitialMount'
import { useParams } from 'react-router-dom'
import { notNull } from '@gap/utils/utils'
import { PlatformContainer } from 'reusable/containers/PlatformContainer'
import { useRegionRouterStore } from 'features/main/RegionRouterStore'
import Typography from '@mui/material/Typography'
import { themeConfig } from '@gap/shared/theme'
import Box from '@mui/material/Box'
import TextFieldEmail from 'reusable/mui/text-field-email/TextFieldEmail'
import ButtonWithProgress from 'reusable/mui/progress-button/ButtonWithProgress'
import FlexAlignRight from 'reusable/mui/utilities/FlexAlignRight'
import AuthVerification from 'auth/verification/AuthVerification'
import AuthShareNames from './AuthShareNames'
import ArrowRightAltOutlined from '@mui/icons-material/ArrowRightAltOutlined'
import AuthLinkExpired from 'auth/validate/AuthInvalidToken'
import Divider from '@mui/material/Divider'
import Button from '@mui/material/Button'
import Stack from '@mui/material/Stack'
import AvatarWithName from 'reusable/mui/avatar-with-name/AvatarWithName'
import Tooltip from '@mui/material/Tooltip'
import onEnterProp from 'reusable/utils/on-enter-prop'
import AccessDenied from 'features/access-denied/AccessDenied'

export default observer(function AuthShare() {
   const authStore = useAuthStore()
   const regionRouterStore = useRegionRouterStore()
   const { token } = useParams()
   const authShareStore = useStore(sp => new AuthShareStore(sp), { authStore, token: notNull(token, 'Invalid url parameters'), regionRouterStore })

   useInitialMount(() => {
      authShareStore.load()
   })

   if (authShareStore.loader.isLoading) {
      return <PlatformContainer loadingMessage={authShareStore.loader.loaderText} loading={true} contentCentered={true} />
   }

   if (authShareStore.documentDoesNotExists) {
      return <PlatformContainer contentCentered={true}>
         <AccessDenied type="document-no-longer-available" ParentWrapper={null} />
      </PlatformContainer>
   }

   if (authStore.status === 'verify with mfa code') {
      return <AuthVerification
         method="email"
         onVerify={authShareStore.onVerify}
         email={authShareStore.fields.email}
         customHandlers={{
            resendCode: authShareStore.resendCode,
            backToLogin: authShareStore.backToLogin
         }}
      />
   }

   return <AuthShareStoreProvider store={authShareStore}>
      <CenterFullScreen>
         {authShareStore.status === 'expired-token' && <PlatformContainer contentCentered={true}><AuthLinkExpired canGoHome={true} text="This token has expired" /></PlatformContainer>}
         {authShareStore.status === 'access-denied-token' && <PlatformContainer contentCentered={true}><AuthLinkExpired canGoHome={true} text="You don't have access to this token" /></PlatformContainer>}
         {authShareStore.status === 'usage-exceeded-token' && <PlatformContainer contentCentered={true}><AuthLinkExpired canGoHome={true} text="This token has reached its usage limit" /></PlatformContainer>}
         {authShareStore.status === 'authenticating' && <AuthShareLogin />}
         {authShareStore.status === 'ask-for-names' && <AuthShareNames />}
      </CenterFullScreen>
   </AuthShareStoreProvider>
})

const AuthShareLogin = observer(function AuthShareLogin() {
   const authShareStore = useAuthShareStore()

   return <AuthCardWrapper title="Please enter your email">
      <Typography variant="body1" sx={{ color: themeConfig.primary[900], mt: -2 }}>{authShareStore.introText}</Typography>
      <Box sx={{ mt: 6 }}>
         {authShareStore.sp.authStore.isAuthenticated ? <AuthShareLoggedIn /> : <AuthShareEmailInput />}
      </Box>
      {(!authShareStore.sp.authStore.isAuthenticated || authShareStore.useDifferentEmail) && <FlexAlignRight sx={{ mt: 3 }}>
         <ButtonWithProgress
            icon={<ArrowRightAltOutlined />}
            variant="contained"
            disabled={
               authShareStore.sp.authStore.isAuthenticated
                  ? authShareStore.useDifferentEmail && !authShareStore.fields.email
                  : !authShareStore.fields.email
            }
            onClick={authShareStore.continue}>
            Continue
         </ButtonWithProgress>
      </FlexAlignRight>}

      {!authShareStore.useDifferentEmail && authShareStore.sp.authStore.isAuthenticated && <>
         <Divider sx={{ mt: 6, mb: 1 }} />
         <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="body1">Not you?</Typography>
            <Button variant="text" size="small" onClick={authShareStore.useDifferentEmailClick}>Use a different email</Button>
         </Box>
      </>}
   </AuthCardWrapper>
})

const AuthShareLoggedIn = observer(function AuthShareLoggedIn() {
   const authShareStore = useAuthShareStore()

   // If they clicked use different email, show the email input
   if (authShareStore.useDifferentEmail) {
      return <AuthShareEmailInput />
   }

   return <Box>
      <Typography variant="body1Bold">You're currently logged in as</Typography>
      <Tooltip title="Continue">
         <Box sx={{ p: 2, mt: 1, border: '1px solid', borderColor: themeConfig.light.borderColour, borderRadius: '16px', cursor: 'pointer' }} onClick={authShareStore.continue}>
            <Stack direction="row" spacing={2} alignItems="center">
               <AvatarWithName name={authShareStore.fields.firstName} />
               <Box>
                  <Typography variant="body1" sx={{ color: themeConfig.primary[900] }}>
                     {authShareStore.fields.firstName} {authShareStore.fields.lastName}
                  </Typography>
                  <Typography variant="body2" sx={{ color: themeConfig.primary[600] }}>
                     {authShareStore.fields.email}
                  </Typography>
               </Box>
               <Box sx={{ flexGrow: 1 }} />
               <Button variant="text" size="small" onClick={authShareStore.continue} endIcon={<ArrowRightAltOutlined />}>
                  Continue
               </Button>
            </Stack>
         </Box>
      </Tooltip>
   </Box>
})

const AuthShareEmailInput = observer(function EmailInput() {
   const authShareStore = useAuthShareStore()
   return authShareStore.formHandler.email.bindInput(ip =>
      <TextFieldEmail
         fullWidth
         label="Email address*"
         {...onEnterProp(authShareStore.continue)}
         ip={ip}
         {...onEnterProp(authShareStore.continue)}
      />)
})
